// src/pages/planning/DeptFilter.tsx — department filter pill + popover, ported
// from the prototype bp-filter.jsx. Shared across Snapshot / Matrix / Calendar
// (lifted to App so the choice persists across tabs). `myDept` replaces the
// prototype's CURRENT_USER.dept.

import { useState, type Dispatch, type SetStateAction } from "react";
import { C, F, DEPTS, DEPT_MAP } from "../../theme";
import { Icon } from "../../components/icons";
import { Eyebrow } from "../../components/prims";

export interface Filter { all: boolean; set: string[]; }

export const matchDept = (filter: Filter, deptId: string): boolean => filter.all || filter.set.includes(deptId);

export function DeptFilter({ filter, setFilter, myDept }: { filter: Filter; setFilter: Dispatch<SetStateAction<Filter>>; myDept: string }) {
  const [open, setOpen] = useState(false);
  const label = filter.all ? "All departments"
    : filter.set.length === 1 ? (DEPT_MAP[filter.set[0]]?.name ?? "1 department")
    : `${filter.set.length} departments`;
  const toggle = (id: string) => setFilter((f) => {
    if (f.all) return { all: false, set: [id] };
    const has = f.set.includes(id);
    const next = has ? f.set.filter((x) => x !== id) : [...f.set, id];
    return { all: false, set: next.length ? next : [myDept] };
  });
  return (
    <div style={{ position: "relative" }}>
      <button type="button" onClick={() => setOpen((o) => !o)} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 10px 7px 9px", borderRadius: 11, border: `1.5px solid ${filter.all ? C.hairline : C.brassLt}`, background: filter.all ? C.card : C.brassSurf, cursor: "pointer" }}>
        <Icon.filter s={14} c={C.brassDk} />
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {!filter.all && filter.set.slice(0, 3).map((id) => <span key={id} style={{ width: 9, height: 9, borderRadius: 3, background: DEPT_MAP[id]?.fg ?? C.inkMute }} />)}
          <span style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 800, color: C.ink }}>{label}</span>
        </span>
        <Icon.chevD s={14} c={C.inkMute} style={{ transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div style={{ position: "absolute", top: "calc(100% + 7px)", right: 0, width: 288, background: C.card, borderRadius: 14, border: `1px solid ${C.hairline}`, boxShadow: C.shadowL, padding: 12, zIndex: 41 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
              <Eyebrow>Show departments</Eyebrow>
              <div style={{ flex: 1 }} />
              <button type="button" onClick={() => setFilter({ all: false, set: [myDept] })} style={{ fontFamily: F.body, fontSize: 11, fontWeight: 800, color: C.brassDk, background: C.brassBg, border: "none", borderRadius: 7, padding: "4px 8px", cursor: "pointer" }}>Just mine</button>
              <button type="button" onClick={() => setFilter((f) => ({ all: true, set: f.set }))} style={{ fontFamily: F.body, fontSize: 11, fontWeight: 800, color: filter.all ? "#FFF" : C.inkSoft, background: filter.all ? C.brassDk : C.paperWarm, border: "none", borderRadius: 7, padding: "4px 8px", cursor: "pointer" }}>All</button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {DEPTS.map((d) => {
                const on = !filter.all && filter.set.includes(d.id);
                const mine = d.id === myDept;
                return (
                  <button key={d.id} type="button" onClick={() => toggle(d.id)} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 999, cursor: "pointer", border: `1.5px solid ${on ? d.fg : d.bd}`, background: on ? d.bg : C.card, color: d.fg, fontFamily: F.body, fontSize: 11, fontWeight: 800, opacity: filter.all ? 0.55 : 1 }}>
                    <span style={{ width: 7, height: 7, borderRadius: 4, background: d.fg }} />
                    {d.short}{mine && <span style={{ fontSize: 9, opacity: 0.7 }}>(you)</span>}
                    {on && <Icon.check s={12} c={d.fg} />}
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 10, paddingTop: 9, borderTop: `1px solid ${C.hairlineLt}`, fontFamily: F.body, fontSize: 10.5, color: C.inkMute, display: "flex", alignItems: "center", gap: 6 }}>
              <Icon.eye s={12} c={C.inkMute} /> Tap a department to reveal its lane across every view.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
